import { useState, useContext, useCallback } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useCampaigns = () => {
  const { api, userID } = useContext(GlobalContext);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

 const postRequest = useCallback(
  async (endpoint, body) => {
    try {
      const response = await api.post(endpoint, body);
      return response?.data;
    } catch (error) {
      console.error(
        "API Error:",
        error?.response?.data || error
      );
      return null;
    }
  },
  [api]
);

  const fetchCampaigns = useCallback(async () => {
    if (!userID) return;

    try {
      setLoading(true);

      const data = await postRequest("/Campaign/getCampaigns", {
        userid: userID,
        campaignName: "string",
      });

      if (data?.responseCode === "00") {
        setCampaigns(data?.data || []);
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      console.error("Campaign API Error:", err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [userID, postRequest]);

  const editCampaign = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest("/Campaign/editCampaign", payload);

        return data;
      } catch (err) {
        console.error("Edit Campaign Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

  const changeCampaignStatus = useCallback(
    async (campaignId, status) => {
      try {
        setLoading(true);

        const data = await postRequest("/Campaign/changeCampaignStatus", {
          userid: userID,
          secret: "string",
          campaignId,
          status: status === "Y" ? "N" : "Y",
        });

        return data;
      } catch (err) {
        console.error("Change Status Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, postRequest],
  );

  const deleteCampaign = useCallback(
    async (campaignId) => {
      try {
        setLoading(true);

        const data = await postRequest("/Campaign/deleteCampaign", {
          userid: userID,
          secret: "string",
          campaignId,
          status: "string",
        });

        return data;
      } catch (err) {
        console.error("Delete Campaign Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, postRequest],
  );

  const createCampaign = useCallback(
    async (payload) => {
      try {
        setLoading(true);

        const data = await postRequest(
          "/Campaign/v2.0/createCampaign",
          payload,
        );

        return data;
      } catch (err) {
        console.error("Create Campaign Error:", err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postRequest],
  );

  const getCampaigns = useCallback(
  async (phoneNumberId = "") => {
    if (!userID) return [];

    try {
      setLoading(true);

      const data = await postRequest("/Campaign/v1.0/getCampaigns", {
        userid: userID,
        campaignName: "string",
        phoneNumberId,
      });

      if (data?.responseCode === "00") {
        const list = data?.data || [];
        setCampaigns(list);
        return list;
      }

      setCampaigns([]);
      return [];
    } catch (err) {
      console.error("Get Campaigns Error:", err);
      setCampaigns([]);
      return [];
    } finally {
      setLoading(false);
    }
  },
  [postRequest, userID],
);

  return {
    campaigns,
    loading,
    setLoading,
    fetchCampaigns,
    editCampaign,
    changeCampaignStatus,
    deleteCampaign,
    createCampaign,
    getCampaigns,
  };
};

export default useCampaigns;
